/**
 * The page registry — which pages exist, and in which languages.
 *
 * One list, and every part of the multilingual system reads it rather than
 * keeping its own copy: routing (`App.jsx` decides whether `/el/x` renders or
 * redirects), the language switcher, internal link resolution, hreflang
 * generation, the sitemap check, and the tests. This is the same arrangement
 * `src/content/guides/registry.js` already uses for guides, for the same
 * reason — a fact restated in five places is a fact that will disagree with
 * itself in five places.
 *
 * `src/lib/language.js` owns the *shape* of a localized URL (the `/el` prefix).
 * This module owns *availability*, which is a different question with a
 * different answer per page, and combines the two.
 *
 * ── What `languages` means ────────────────────────────────────────────────
 * A language is listed for a page when that page can actually be SERVED in it:
 * a static document exists, a rewrite routes to it, and the sitemap can carry
 * it. It does **not** mean a translation file exists.
 *
 * That distinction is the whole point. `src/locales/langs/el/` has held
 * complete, human-written Greek for the homepage and all five product pages for
 * some time — reachable today only through `?lang=el` or a stored preference,
 * rendered on the English URL. None of it has a crawlable Greek URL yet, so
 * none of these pages lists `"el"`. Listing one before its document exists would
 * put a URL in the sitemap that 404s and an hreflang pointing at nothing, which
 * is worse than having no Greek page at all.
 *
 * Enabling Greek for a page is therefore a deliberate four-part change, in one
 * commit: add `"el"` here, add the static document, add the rewrite to BOTH
 * copies of `vercel.json`, add the sitemap entry. `registry.test.mjs` fails
 * while those disagree.
 *
 * ── A note for whoever adds the first Greek page ──────────────────────────
 * Vercel evaluates `redirects` BEFORE `rewrites`, regardless of file order. A
 * blanket `/el/(.*)` redirect would therefore pre-empt every Greek rewrite ever
 * added, no matter where it sits in the file, and Greek would be unreachable
 * with nothing in the config looking wrong. That is why unavailable Greek paths
 * are resolved in the router (see `App.jsx`) and not by a catch-all redirect.
 */

import { GUIDES, guideLanguages, guideSlug } from "../guides/registry.js";
import { GUIDES_BASE_PATH, SITE_ORIGIN, guidePath } from "../guides/guideModel.js";
import {
  DEFAULT_LANGUAGE,
  stripLanguagePrefix,
  withLanguagePrefix,
} from "../../lib/language.js";

/**
 * Every page that is not a guide.
 *
 * `sitemap` is whether the page belongs in `public/sitemap.xml`, which is not
 * the same as whether it is routed:
 *
 *   - `/home` renders the homepage but is not canonical — `/` is. It stays a
 *     real route for existing links and bookmarks (see `navScope.homeHrefFor`)
 *     and out of the sitemap, exactly as it is today.
 *   - `/guides` is `noindex, nofollow` with a self-referencing canonical and is
 *     deliberately absent from the sitemap; the page documents why.
 */
const STATIC_PAGES = [
  // The homepage is the first page with a real Greek URL. `/el/` is a genuine
  // static document — `el/index.html`, generated by `scripts/build-home.mjs`
  // from the Greek locale — not the English document relabelled, so listing
  // `"el"` here is a statement about what the server can serve, exactly as the
  // note above requires.
  { path: "/", languages: ["en", "el"], sitemap: true },
  { path: "/home", languages: ["en"], sitemap: false },

  // The five product pages, each with a real Greek document generated by
  // `scripts/build-pages.mjs` from the Greek locale — `el/wedding-planning.html`
  // and its four siblings. As with the homepage above, listing `"el"` here is a
  // statement about what the server can serve, not about a translation existing.
  { path: "/wedding-photo-sharing", languages: ["en", "el"], sitemap: true },
  { path: "/digital-wedding-invitation", languages: ["en", "el"], sitemap: true },
  { path: "/wedding-planning", languages: ["en", "el"], sitemap: true },
  { path: "/wedding-rsvp", languages: ["en", "el"], sitemap: true },
  { path: "/wedding-seating", languages: ["en", "el"], sitemap: true },
  // The guides index, now that the guide corpus is bilingual in full. It has
  // a real document in both languages (scripts/build-static-pages.mjs) and
  // stays OUT of the sitemap, because its SEO policy is unchanged: noindex,
  // nofollow, self-referencing canonical. `sitemap: false` and
  // `languages: [...]` answer different questions, which is exactly why they
  // are separate fields.
  { path: GUIDES_BASE_PATH, languages: ["en", "el"], sitemap: false },

  // Contact and the three legal pages, each with a real document in BOTH
  // languages generated by `scripts/build-static-pages.mjs`. These four are the
  // only pages here whose ENGLISH document is also generated: they never had
  // one, and were served `index.html` — the homepage, canonical included —
  // through vercel.json's catch-all. The generator's own note says the rest.
  //
  // Their Greek came from two different places, which is why they are one group
  // here and two renderers there: /contact from `contact.json`, the three legal
  // pages from the `*-gr.js` content modules, ~105KB of human-written Greek
  // that had no URL of its own until now.
  { path: "/contact", languages: ["en", "el"], sitemap: true },
  { path: "/terms", languages: ["en", "el"], sitemap: true },
  { path: "/privacy-policy", languages: ["en", "el"], sitemap: true },
  { path: "/cookie-policy", languages: ["en", "el"], sitemap: true },
];

/**
 * The guides, derived from their own registry so the two can never disagree
 * about which guides exist or which languages they are written in.
 *
 * A guide's languages are the content modules it actually has — `entry.en`,
 * `entry.el` — which is what makes English-only, Greek-only and bilingual
 * guides all expressible without a special case. All fourteen have both today;
 * a fifteenth may ship English-first and acquire its Greek URL later, with
 * nothing here to change.
 *
 * Guides are also the only pages whose path DIFFERS between languages, which is
 * what `paths` exists for: a translated guide has its own Greek slug, so the
 * pair is `/guides/wedding-seating-plan` ↔ `/el/guides/plano-trapezion-gamou`
 * rather than the same words behind a prefix. `path` stays the identity — the
 * English path — and everything derived reads `basePathFor` instead of
 * assuming one path serves every language.
 */
const guidePages = () =>
  GUIDES.map((entry) => ({
    path: guidePath(entry.slug),
    paths: Object.fromEntries(
      guideLanguages(entry).map((language) => [
        language,
        guidePath(guideSlug(entry, language)),
      ]),
    ),
    languages: guideLanguages(entry),
    sitemap: true,
  }));

/** Every page on the site, language-neutral paths. */
export const PAGES = [...STATIC_PAGES, ...guidePages()];

/**
 * The registry entry for a path, or `undefined`.
 *
 * The path may be language-prefixed or not — `/el/wedding-planning` and
 * `/wedding-planning` are the same page, which is why pages are keyed by their
 * language-neutral path.
 *
 * Every resolver here takes the registry as an optional last argument,
 * defaulting to the real one. Tests pass a fixture to exercise English-only,
 * Greek-only and bilingual pages without shipping a page that does not exist.
 */
export const getPageByPath = (pathname, pages = PAGES) => {
  const base = stripLanguagePrefix(pathname);
  return pages.find(
    (page) =>
      page.path === base ||
      // A translated guide answers to its Greek path too. Without this,
      // `/el/guides/plano-trapezion-gamou` is an unknown page: the router
      // redirects it to English, hreflang comes back empty and the document
      // that was generated for it is never reachable.
      Object.values(page.paths ?? {}).includes(base),
  );
};

/**
 * The language-neutral path a page uses in a language — its own where it has
 * one, the identity path otherwise.
 *
 * Every URL this module produces goes through here. Before localized guide
 * slugs, `page.path` was the answer for every language and the prefix carried
 * the whole difference; now the canonical, the alternates, the sitemap entry
 * and the internal link each have to ask which path THIS language uses, and
 * they must all ask the same way or a guide's canonical and its hreflang will
 * name two different URLs.
 */
export const basePathFor = (page, language) =>
  page?.paths?.[language] ?? page?.path;

/** The languages a page is available in, `[]` for an unknown path. */
export const languagesFor = (pathname, pages = PAGES) =>
  getPageByPath(pathname, pages)?.languages ?? [];

/**
 * Whether a page exists in a language. Accepts a registry entry or a path, so
 * callers that already resolved the page do not resolve it twice.
 */
export const hasLocalizedPage = (page, language, pages = PAGES) => {
  const entry = typeof page === "string" ? getPageByPath(page, pages) : page;
  return Boolean(entry?.languages.includes(language));
};

/**
 * Whether a page exists in more than one language — and so whether its
 * UNPREFIXED URL is authoritatively English.
 *
 * This is the predicate the language authority rests on, and it is deliberately
 * a question about the page rather than about the site:
 *
 *   - A page with a Greek URL has somewhere to be Greek. Its English URL is
 *     therefore English for everyone, whatever `?lang=` or a stored preference
 *     says, exactly as `/el/…` is Greek for everyone. That is the whole point
 *     of putting language in the URL, and it is what lets both halves of the
 *     pair be indexed as themselves.
 *
 *   - A page with only an English URL has nowhere else to be. Pinning it would
 *     not move anyone to a Greek page; it would only delete the Greek rendering
 *     that `?lang=el` and the switcher still reach today. Today that is the
 *     `/home` alias, and nothing else: the guides index used to be the other
 *     one — four Greek strings framing fourteen English articles — and stopped
 *     being an exception the moment it got a Greek URL of its own, exactly as
 *     the paragraph below predicts.
 *
 * The exception is therefore not a carve-out anyone has to remember to remove:
 * it evaporates on its own as pages are translated, and the day the last
 * English-only page gets a Greek URL this returns `true` everywhere and the
 * rule is simply "unprefixed is English".
 */
export const hasLocalizedCounterpart = (pathname, pages = PAGES) =>
  languagesFor(pathname, pages).length > 1;

/**
 * The path to use for a page in a language.
 *
 * Availability-aware, and the ONE place the fallback decision is made: when the
 * page has no version in the requested language, the default-language path is
 * returned. A working English page is a better destination for an internal
 * content link than a 404 — and while a Greek page's own links should stay
 * Greek, they cannot point at Greek pages that do not exist yet.
 *
 *   ("/wedding-rsvp", "el")  → "/el/wedding-rsvp"   once Greek exists
 *   ("/wedding-rsvp", "el")  → "/wedding-rsvp"      until it does
 *
 * The language SWITCHER deliberately does not use this — being quietly handed
 * an English URL is the right answer for a link inside a sentence and the wrong
 * one for a control that promises Greek. It asks `hasLocalizedPage` instead;
 * see `useLanguageSwitcher`.
 */
export const localizedPath = (pathname, language, pages = PAGES) => {
  const base = stripLanguagePrefix(pathname);
  const page = getPageByPath(base, pages);
  const target = hasLocalizedPage(page, language, pages)
    ? language
    : DEFAULT_LANGUAGE;
  // `basePathFor` and the prefix are resolved together: a translated guide
  // changes BOTH, and taking the slug from one language with the prefix of
  // another produces a URL that has never existed.
  return withLanguagePrefix(basePathFor(page, target) ?? base, target);
};

/** Spelt-out alias of `localizedPath`, for call sites that read better with it. */
export const getLocalizedPath = localizedPath;

/**
 * The absolute, self-referencing canonical URL for a page in a language.
 *
 * Resolves the page first so a guide canonicalises to its own language's slug.
 * An unregistered path keeps the old behaviour — prefix what you were given —
 * which is what the tests' fixture registries and the `/home` alias rely on.
 */
export const canonicalUrlFor = (pathname, language, pages = PAGES) => {
  const base = stripLanguagePrefix(pathname);
  const page = getPageByPath(base, pages);
  return `${SITE_ORIGIN}${withLanguagePrefix(basePathFor(page, language) ?? base, language)}`;
};

/**
 * The `<link rel="alternate">` set for a page, or `[]` when it has no genuine
 * counterpart.
 *
 * The rules, and why each one is a rule:
 *
 *   - **Only real pages.** An alternate pointing at a URL that does not exist
 *     is the single most common way a multilingual launch fails: Google drops
 *     the whole cluster rather than the one bad tag.
 *   - **Nothing at all for a single-language page.** A set naming only itself
 *     asserts nothing, and a lone `x-default` even less. Silence is correct.
 *   - **Reciprocal by construction.** Both language versions call this function
 *     with the same page, so they emit the same set. Reciprocity is not
 *     something a human has to remember; it is the only thing this can produce.
 *   - **`x-default` is the English URL** — the default language, the root of the
 *     site, and the wider audience. Not a language selector, which we do not
 *     have and would not want at `/`.
 *
 * Canonicals are NOT built here. Each page's canonical is self-referencing, and
 * a Greek page must never canonicalise to its English counterpart — that would
 * ask Google not to index it.
 */
export const alternatesFor = (pathname, pages = PAGES) => {
  const base = stripLanguagePrefix(pathname);
  const languages = languagesFor(base, pages);
  if (languages.length < 2) return [];

  // Each alternate is built from ITS OWN language's path, which is the whole
  // reason a translated guide's hreflang points somewhere real: the Greek entry
  // names the Greek slug, the English entry names the English one, and both
  // sides of the pair produce the identical set because both resolve the same
  // registry page.
  const alternates = languages.map((language) => ({
    hreflang: language,
    href: canonicalUrlFor(base, language, pages),
  }));

  if (languages.includes(DEFAULT_LANGUAGE)) {
    alternates.push({
      hreflang: "x-default",
      href: canonicalUrlFor(base, DEFAULT_LANGUAGE, pages),
    });
  }
  return alternates;
};

/**
 * Every URL that belongs in `public/sitemap.xml`, one per page per available
 * language. The sitemap itself stays hand-maintained — its `lastmod` values are
 * content-change dates taken from git, which no generator can infer — so this
 * is what `registry.test.mjs` checks the file against, in both directions:
 * nothing missing, and no Greek URL for a page that has no Greek version.
 */
export const sitemapUrls = (pages = PAGES) =>
  pages
    .filter((page) => page.sitemap)
    .flatMap((page) =>
      page.languages.map((language) => ({
        path: withLanguagePrefix(basePathFor(page, language), language),
        language,
        url: canonicalUrlFor(page.path, language, pages),
      })),
    );
